import { CheckCircle2, AlertCircle, XCircle, Info } from "lucide-react";
import { Routes, Route, Navigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Home from "./pages/Home";
import DiagnosticsOverview from "./pages/diagnostics/Overview";
import WebsiteDiagnosticTool from "./pages/diagnostics/WebsiteTool";
import MarketingTool from "./pages/diagnostics/MarketingTool";
import BusinessAssessmentTool from "./pages/diagnostics/BusinessAssessmentTool";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthGuard from "./components/AuthGuard";
import { useAuth } from "./context/AuthContext";
import { Slide, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PublicProposalPage from "./components/PublicProposalPage";
import Settings from "./pages/Settings";


// Job Board Pages
import OverviewDashboard from "./pages/job-board/OverviewDashboard";
import JobsOverview from "./pages/job-board/JobsOverview";
import JobsList from "./pages/job-board/JobsList";
import JobDetail from "./pages/job-board/JobDetail";
import JobForm from "./pages/job-board/JobForm";
import ApplicantsOverview from "./pages/job-board/ApplicantsOverview";
import ApplicantsList from "./pages/job-board/ApplicantsList";
import ApplyForm from "./pages/job-board/ApplyForm";

// Billing Pages
import InvoicesModule from "./pages/billing/InvoicesModule";
import ProposalModule from "./pages/billing/ProposalModule";

function App() {
  const { token } = useAuth();
  const isAuthenticated = !!token;

  return (
    <div className="flex min-h-screen bg-slate-50">
      {isAuthenticated && <Sidebar />}
      <main className={`w-full flex flex-col ${isAuthenticated ? "flex-1" : "items-center justify-center"}`}>
        <Routes>
          <Route
            path="/"
            element={
              <AuthGuard>
                <Home />
              </AuthGuard>
            }
          />
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} 
          />
          <Route 
            path="/register" 
            element={isAuthenticated ? <Navigate to="/" replace /> : <Register />} 
          />
          <Route
            path="/diagnostics/overview"
            element={
              <AuthGuard>
                <DiagnosticsOverview />
              </AuthGuard>
            }
          />
          <Route
            path="/diagnostics/website-tool"
            element={
              <AuthGuard>
                <WebsiteDiagnosticTool />
              </AuthGuard>
            }
          />
          <Route
            path="/diagnostics/marketing-tool"
            element={
              <AuthGuard>
                <MarketingTool />
              </AuthGuard>
            }
          />
          <Route
            path="/diagnostics/business-assessment"
            element={
              <AuthGuard>
                <BusinessAssessmentTool />
              </AuthGuard>
            }
          />
          
          {/* Job Board Module */}
          <Route
            path="/job-board/overview"
            element={
              <AuthGuard>
                <OverviewDashboard />
              </AuthGuard>
            }
          />
          
          {/* Jobs Submodule */}
          <Route
            path="/job-board/jobs/overview"
            element={
              <AuthGuard>
                <JobsOverview />
              </AuthGuard>
            }
          />
          <Route
            path="/job-board/jobs"
            element={
              <AuthGuard>
                <JobsList />
              </AuthGuard>
            }
          />
          <Route
            path="/job-board/jobs/create"
            element={
              <AuthGuard roles={["Admin", "System"]}>
                <JobForm />
              </AuthGuard>
            }
          />
          <Route
            path="/job-board/jobs/:id"
            element={
              <AuthGuard>
                <JobDetail />
              </AuthGuard>
            }
          />
          <Route
            path="/job-board/jobs/:id/edit"
            element={
              <AuthGuard roles={["Admin", "System"]}>
                <JobForm />
              </AuthGuard>
            }
          />

          {/* Applicants Submodule */}
          <Route
            path="/job-board/applicants/overview"
            element={
              <AuthGuard>
                <ApplicantsOverview />
              </AuthGuard>
            }
          />
          <Route
            path="/job-board/applicants/job/:jobId"
            element={
              <AuthGuard>
                <ApplicantsList />
              </AuthGuard>
            }
          />
          <Route path="/job-board/apply/:jobId" element={<ApplyForm />} />

          {/* Billing & Proposals Module */}
          <Route
            path="/invoices"
            element={
              <AuthGuard>
                <InvoicesModule />
              </AuthGuard>
            }
          />
          <Route
            path="/proposals/*"
            element={
              <AuthGuard>
                <ProposalModule />
              </AuthGuard>
            }
          />

          <Route path="/proposal/:token" element={<PublicProposalPage />} />

          {/* Settings Module */}
          <Route
            path="/settings/*"
            element={
              <AuthGuard>
                <Settings />
              </AuthGuard>
            }
          />
        </Routes>
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          transition={Slide}
          limit={3}
          toastClassName="!rounded-3xl !shadow-2xl !border !border-slate-100 !p-4 !flex !items-center !gap-4"
          bodyClassName="!p-0 !m-0 text-sm font-semibold text-slate-700 font-sans"
          icon={({ type }) => {
            switch (type) {
              case 'success':
                return <div className="w-12 h-12 shrink-0 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 font-bold"><CheckCircle2 size={24} /></div>;
              case 'error':
                return <div className="w-12 h-12 shrink-0 bg-red-50 rounded-2xl flex items-center justify-center text-red-600 font-bold"><XCircle size={24} /></div>;
              case 'warning':
                return <div className="w-12 h-12 shrink-0 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 font-bold"><AlertCircle size={24} /></div>;
              case 'info':
                return <div className="w-12 h-12 shrink-0 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 font-bold"><Info size={24} /></div>;
              default:
                return null;
            }
          }}
        />
      </main>
    </div>
  );
}

export default App;
